import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { BackendApiService } from '../../../services/backend-api.service';
import { NotificationService } from '../../../services/notification.service';
import { Document, OcrText } from '../document/document';
import { DocumentThumbnailService } from '../document-thumbail.service';
import { SafeResourceUrl } from '@angular/platform-browser';
import { PictureDialogComponent } from '../document-thumbnail/picture-dialog/picture-dialog.component';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { DocumentService } from '../document.service';
import { PdfViewerDialogInput } from '../../../dialogs/pdf-viewer-dialog/pdf-viewer-dialog';
import { PdfViewerDialogService } from '../../../dialogs/pdf-viewer-dialog/pdf-viewer-dialog.service';
import { from, Observable, of, switchMap } from 'rxjs';
import { MatTooltip } from '@angular/material/tooltip';
import { PhitoUtils } from '../../../utils/phito-utils';
import { PhitoSessionService } from '../../../services/phito-session.service';
import { DialogService } from '../../../services/dialog.service';

@Component({
    selector: 'phito-file-downloader-button',
    imports: [CommonModule, MatButtonModule, MatIconModule, MatDialogModule, MatTooltip],
    templateUrl: './file-downloader-button.component.html',
    styleUrl: './file-downloader-button.component.scss',
})
export class FileDownloaderButtonComponent implements OnChanges {
    private api = inject(BackendApiService);
    private notificationService = inject(NotificationService);
    private documentThumbnailService = inject(DocumentThumbnailService);
    private documentService = inject(DocumentService);
    private dialog = inject(MatDialog);
    private pdfViewerDialogService = inject(PdfViewerDialogService);
    private sessionService = inject(PhitoSessionService);
    private dialogService = inject(DialogService);

    @Input({ required: true }) documentHexId: string;
    @Input() disableShadow: boolean = false;
    @Input() width: string = '300px';
    @Input() hasAccess = true;

    document: Document;
    url: SafeResourceUrl;
    failedToLoadThumbnail = false;
    background: any;
    fileSize = '0 KB';
    isLoading = true;

    constructor() { }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.documentHexId) {
            this.loadDocument();
        }
    }

    isPdfOrImageOrText(): boolean {
        return this.isPdf() || this.isPicture() || this.isText() || this.isOfficeDocument();
    }

    loadDocument() {
        if (this.documentHexId) {
            this.isLoading = true;
            this.api.getOne('documents', this.documentHexId).subscribe({
                next: (document: Document) => {
                    this.document = document;
                    this.fileSize = PhitoUtils.formatBytes(this.document.size, 0);
                    this.isLoading = false;
                    this.loadThumbnail();
                },
                error: (error) => {
                    this.isLoading = false;
                    this.notificationService.errorMessageGet('Failed to load document', error, null);
                },
            });
        }
    }

    loadThumbnail(sender?: string) {
        if (sender) {
            console.log('loadThumbnail sender: %s', sender);
        }
        if (this.documentHexId && this.document?.fileHexId && this.document?.thumbnailHexId) {
            this.documentThumbnailService.getThumbnail(this.document.thumbnailHexId, this.document.mimeType).subscribe({
                next: (safeUrl) => {
                    this.url = safeUrl;
                    this.failedToLoadThumbnail = false;
                },
                error: () => {
                    this.failedToLoadThumbnail = true;
                },
            });
        } else {
            this.failedToLoadThumbnail = true;
        }
    }

    public openPdf(deliveryBillHexId: string): Observable<boolean> {
        const title = 'Lieferschein';
        const fileName = 'Lieferschein.pdf';
        const pdfInput = new PdfViewerDialogInput(title, {
            download: { url: `phitodeliverybills/${deliveryBillHexId}/pdf` },
            dateiname: fileName,
        });

        return this.pdfViewerDialogService.openPdfDialog(pdfInput);
    }

    getBackgroundImageUrl() {
        return `url("${this.url}")`;
    }

    downloadFile() {
        if (this.hasAccess) {
            this.documentService.downloadFile(
                this.document?.fileHexId,
                this.document?.name || 'download',
                this.document?.mimeType || 'application/octet-stream',
            );
        }
    }

    isPicture(): boolean {
        return this.document?.mimeType?.startsWith('image');
    }

    isPdf(): boolean {
        return this.document?.mimeType?.toLowerCase() === 'application/pdf';
    }

    isText(): boolean {
        return this.document?.mimeType?.startsWith('text/') || this.document?.mimeType?.toLowerCase() === 'application/javascript'
            || this.document?.mimeType?.toLowerCase() === 'application/json'
            || this.document?.mimeType?.toLowerCase() === 'application/xml'
            || this.document?.mimeType?.toLowerCase() === 'application/octet-stream';
    }

    isOfficeDocument(): boolean {
        return this.isWord() || this.isExcel() || this.isPowerPoint();
    }

    isWord(): boolean {
        const mimeType = this.document?.mimeType?.toLowerCase();
        return mimeType === 'application/msword' || mimeType?.includes('officedocument.wordprocessing');
    }

    isExcel(): boolean {
        const mimeType = this.document?.mimeType?.toLowerCase();
        return mimeType?.includes('application/vnd.ms-excel') || mimeType?.includes('officedocument.spreadsheet');
    }

    isPowerPoint(): boolean {
        const mimeType = this.document?.mimeType?.toLowerCase();
        return mimeType?.includes('application/vnd.ms-powerpoint') || mimeType?.includes('officedocument.presentation');
    }

    showFile() {
        if (this.hasAccess) {
            if (this.isPicture()) {
                this.openPictureDialog();
            } else if (this.isPdf()) {
                this.showPdf();
            } else if (this.isText()) {
                this.showText();
            } else if (this.isOfficeDocument()) {
                this.showOfficePdf();
            }
        }
    }

    showText() {
        this.api.getFile('documentdata/filefordocument/' + this.document.id).pipe(
            switchMap(blob => from(blob.text()))
        ).subscribe({
            next: (text: string) => {
                this.dialogService.confirm(this.document.name, text, { width: '80vw', minWidth: '1000px' });
            },
            error: err => {
                this.notificationService.errorMessageGet('Fehler!', err);
            }
        });
    }

    showPdf() {
        const pdfInput = new PdfViewerDialogInput(this.document.name, {
            download: { url: 'documentdata/filefordocument/' + this.document.id },
            dateiname: this.document.name,
        });
        this.pdfViewerDialogService.openPdfDialog(pdfInput).subscribe();
    }

    showOfficePdf() {
        const pdfInput = new PdfViewerDialogInput('Vorschau von ' + this.document.name, {
            download: { url: 'documentdata/pdffordocument/' + this.document.id },
            dateiname: this.getPdfFileName(),
        });
        this.pdfViewerDialogService.openPdfDialog(pdfInput).subscribe();
    }

    openPictureDialog() {
        this.dialog.open(PictureDialogComponent, {
            minWidth: '300px',
            data: { document: this.document },
        });
    }

    cutFileNameLength(name: string): string {
        name = name?.replaceAll('\n', '').replaceAll('\r', '');
        if (name && name.length > 16) {
            return name.substring(0, 8) + '...' + name.substring(name.length - 8);
        } else {
            return name;
        }
    }

    private getPdfFileName(): string {
        if (!this.document?.name) {
            return 'document.pdf';
        }
        const normalized = this.document.name.trim();
        if (normalized.toLowerCase().endsWith('.pdf')) {
            return normalized;
        }
        return `${normalized}.pdf`;
    }

    showOcrText() {
        if (this.sessionService.isPhitoAdmin() && this.document?.ocrTextId) {
            this.api.getOne('ocrtexts', this.document.ocrTextId).subscribe({
                next: (response: OcrText) => {
                    this.dialogService.confirm('OCR Text', response.text, { minWidth: '1000px' });
                },
                error: err => {
                    this.notificationService.errorMessageGet('Fehler!', err);
                }
            });
        }
    }

}
